"use client";

import { createContext, useContext, useEffect, useRef } from "react";
import { useQueryLoader } from "react-relay";

import { userQuery } from "./useCurrentUser";

import type { FC, ReactNode } from "react";
import type { PreloadedQuery } from "react-relay";
import type { useCurrentUserQuery } from "__generated__/useCurrentUserQuery.graphql";

const CurrentUserContext = createContext<PreloadedQuery<useCurrentUserQuery> | null>(null);

export function useCurrentUserQueryRef(): PreloadedQuery<useCurrentUserQuery> {
  const queryRef = useContext(CurrentUserContext);
  if (queryRef == null) {
    throw new Error("useCurrentUserQueryRef must be used within a CurrentUserProvider");
  }
  return queryRef;
}

interface CurrentUserProviderProps {
  currentUserId?: string;
  children: ReactNode;
}

export const CurrentUserProvider: FC<CurrentUserProviderProps> = ({ currentUserId, children }) => {
  const [queryRef, loadQuery] = useQueryLoader<useCurrentUserQuery>(userQuery);
  const loadedUserId = useRef<string | null>(currentUserId ?? null);

  // Re-fetch when the authenticated user changes (e.g. org switch).
  useEffect(() => {
    if (currentUserId && currentUserId !== loadedUserId.current) {
      loadedUserId.current = currentUserId;
      loadQuery({}, { fetchPolicy: "network-only" });
    }
  }, [currentUserId, loadQuery]);

  // Initial render-time load.
  //
  // On organization switch, this component gets unmounted and remounted, in App.tsx.  This
  // should cause the query to be reloaded as useQueryLoader is automatically disposed on
  // unmount.  However, I had to add the fetchPolicy: "network-only" to ensure the query is
  // reloaded for some reason.  I don't fully understand why this is necessary, so just
  // documenting it here for now.  Relatedly, this means that the useEffect above is probably
  // not practically doing anything, but I'm leaving it in in case there are situations where
  // the authenticated user can change without this component being unmounted/remounted to
  // ensure the current user query is reloaded.
  if (!queryRef) {
    // Skip the query when there is no authenticated user to avoid a tight
    // refetch loop: the unauthenticated GraphQL error triggers authLogout,
    // which resets Redux state, which re-mounts this component, repeating.
    if (currentUserId) {
      loadQuery({}, { fetchPolicy: "network-only" });
    }
    return null;
  }

  return <CurrentUserContext.Provider value={queryRef}>{children}</CurrentUserContext.Provider>;
};
